import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../../common/hooks/hooks";
import { closeProductModal } from "./productModalSlice";
import { useGetProductByIdQuery } from "../../../api/productApi";
import { useVariationSelection } from "../../../hooks/useVariationSelection";
import ProductInfoGallery from "../ProductInfo/ProductInfoGallery";
import ProductInfoDetails from "../ProductInfo/ProductInfoDetails/ProductInfoDetails";
import Icon from "../../../../../../common/components/Icon/Icon";

// SKELETON IMPORTS
import ProductInfoGallerySkeleton from "../ProductInfo/ProductInfoGallerySkeleton";
import ProductInfoDetailsSkeleton from "../ProductInfo/ProductInfoDetails/ProductInfoDetailsSkeleton";

// --- Sub-Component: Skeleton Loader ---
const ProductModalSkeleton: React.FC = () => (
  <div className="product-modal__content-grid">
    <ProductInfoGallerySkeleton />
    <ProductInfoDetailsSkeleton />
  </div>
);

// --- Main Modal Component ---
const ProductModal: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const dispatch = useAppDispatch();

  // 1. Destructure initialAttributes from state
  const { isOpen, productId, initialAttributes } = useAppSelector(
    (state) => state.productModal
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const portalRoot = document.body;

  const handleClose = () => {
    dispatch(closeProductModal());
  };

  // --- Data Fetching for the main product ---
  const {
    data: product,
    isFetching: isProductFetching,
    isError: isProductError,
    // The assertion productId as number is safe here because
    // the query is skipped when productId is falsy.
  } = useGetProductByIdQuery(productId as number, {
    skip: !productId,
  });

  // --- Consume the custom hook for all variation logic ---
  const {
    selectedAttributes,
    selectedVariation,
    handleAttributeSelect,
    isSelectionFetching,
  } = useVariationSelection(product, initialAttributes); // 2. Pass initialAttributes here

  const isStaleData = product && productId !== product.id;

  // The component is considered "loading" if product data is fetching,
  // variation data is fetching, translations aren't ready, or the data is for a different product.
  const isComponentLoading =
    isProductFetching ||
    isSelectionFetching ||
    !areTranslationsReady ||
    isStaleData;

  // --- Effect for focus managing body class ---
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      document.body.classList.add("product-modal-open");
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.body.classList.remove("product-modal-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // --- Centralized Content Rendering Logic ---
  let headerContent: React.ReactNode;
  let bodyContent: React.ReactNode;

  if (isComponentLoading) {
    // State 1: isProductFetching, translations are not ready, stale data detected
    // or variation selection hook isn't ready
    headerContent = <Skeleton width={150} />;
    bodyContent = <ProductModalSkeleton />;

    // State 2: isProductFetching, we intentionally ignore this state to avoid flicker
  } else if (isProductError) {
    // State 3: isProductError
    headerContent = t("productModal.errorLoadingProductTitle");
    bodyContent = <p>{t("productModal.errorLoadingProduct")}</p>;
  } else if (!product) {
    // State 4: isProductSuccess, but no product found
    headerContent = t("productModal.productNotFoundTitle");
    bodyContent = <p>{t("productModal.productNotFound")}</p>;
  } else {
    // State 5: isProductSuccess, product found (Happy Path)
    headerContent = product.name;
    bodyContent = (
      <div className="product-modal__content-grid">
        <ProductInfoGallery
          product={product}
          selectedVariation={selectedVariation}
        />
        <ProductInfoDetails
          product={product}
          selectedVariation={selectedVariation}
          selectedAttributes={selectedAttributes}
          onAttributeSelect={handleAttributeSelect}
          showDetailsLink={true}
        />
      </div>
    );
  }

  if (!isOpen || !portalRoot) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={`product-modal-wrapper ${isOpen ? "is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="product-modal__overlay" onClick={handleClose} />
      <div
        ref={panelRef}
        className="product-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-modal-title"
      >
        <div className="product-modal__header">
          <h2 id="product-modal-title" className="product-modal__title">
            {headerContent}
          </h2>
          <button
            onClick={handleClose}
            className="product-modal__close-btn"
            aria-label="Close quick view"
          >
            <Icon iconName="x-mark" className="icon--stroke-thickest" />
          </button>
        </div>
        <div className="product-modal__body">{bodyContent}</div>
      </div>
    </div>,
    portalRoot
  );
};

export default ProductModal;
